import { get, has, merge } from 'lodash';
import {
  CHART_COLORS,
  VEGA_SPEC_AXIS_SETTINGS,
  VEGA_SPEC_LEGEND_SETTINGS,
} from 'constants/chart-spec';
import { CHART_CATEGORIES } from 'constants/chart-categories';
import { generateVegaLabelTextExpression } from 'utils/chart/vega-expressions';
import { generateVegaTooltip } from 'utils/chart/tooltips';
import {
  generateVegaSortParams,
  generateVegaTypeCoercionTransforms,
  generateVegaSortingAndLimitingTransforms,
} from 'utils/chart/transforms';

export default {
  name: '100% Stacked Bar',
  displayName: '100%',
  order: 2,
  flavour: 'discrete',
  category: CHART_CATEGORIES.BAR,
  iconValue: 'bar-100-stacked',
  channels: [
    {
      name: 'x',
      displayName: 'X Axis',
      required: true,
      channelType: 'aggregation',
      intent: 'value-axis',
      multipleSeries: true,
    },
    {
      name: 'y',
      displayName: 'Y Axis',
      required: false,
      channelType: 'category',
      intent: 'category-axis',
    },
    {
      name: 'color',
      displayName: 'Series',
      required: false,
      channelType: 'category',
      intent: 'series',
    },
  ],
  specFn: (encodings, customisations) => {
    const sortParams = generateVegaSortParams(encodings);
    return {
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      style: 'cell',
      data: [
        {
          name: 'transformedData',
          source: 'values',
          transform: [
            ...generateVegaTypeCoercionTransforms('x'),
            ...generateVegaSortingAndLimitingTransforms(encodings),
            {
              type: 'stack',
              groupby: ['y'],
              field: 'x',
              sort: {
                field: [sortParams.color.field, 'tooltip'],
                order: ['ascending', 'ascending'],
              },
              as: ['x_start', 'x_end'],
              offset: 'normalize',
            },
          ],
        },
      ],
      marks: [
        {
          name: 'marks',
          type: 'rect',
          style: ['bar'],
          from: { data: 'transformedData' },
          encode: {
            update: {
              y: { scale: 'y', field: 'y' },
              x: { scale: 'x', field: 'x_end' },
              x2: { scale: 'x', field: 'x_start' },
              fill: { scale: 'color', field: 'color' },
              height: { scale: 'y', band: true },
              tooltip: {
                signal: generateVegaTooltip(encodings, customisations),
              },
            },
          },
        },
      ],
      scales: [
        {
          name: 'x',
          type: 'linear',
          domain: { data: 'transformedData', fields: ['x_start', 'x_end'] },
          zero: true,
          range: [0, { signal: 'width' }],
        },
        {
          name: 'y',
          type: 'band',
          domain: { data: 'transformedData', field: 'y', sort: sortParams.y },
          range: [0, { signal: 'height' }],
          paddingInner: 0.1,
          paddingOuter: 0.05,
        },
        {
          name: 'color',
          type: 'ordinal',
          domain: {
            data: 'transformedData',
            field: 'color',
            sort: sortParams.color,
          },
          range:
            get(customisations.options, 'colorDiscrete.value') || CHART_COLORS,
        },
      ],
      axes: [
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'x',
          orient: 'bottom',
          grid: true,
          tickCount: { signal: 'ceil(width/60)' },
          title: get(encodings, 'x.axis.title', ''),
          format: '.0%',
        }),
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'y',
          orient: 'left',
          grid: false,
          title: get(encodings, 'y.axis.title', ''),
          encode: {
            labels: {
              update: {
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.y,
                    customisations.channels.y
                  ),
                },
              },
            },
          },
        }),
      ],
      legends: !has(encodings, 'color.legend.title')
        ? undefined
        : [
            merge({}, VEGA_SPEC_LEGEND_SETTINGS, {
              title: get(encodings, 'color.legend.title'),
              fill: 'color',
              encode: {
                labels: {
                  update: {
                    text: {
                      signal: generateVegaLabelTextExpression(
                        encodings.color,
                        customisations.channels.color
                      ),
                    },
                  },
                },
              },
            }),
          ],
    };
  },
};
